/**
 * A query-builder "block" wires together the following query components:
 * - header (e.g. "USERS THAT <DID>")
 * - search-bar input (e.g. "Select event")
 * - dropdown (e.g. event selector, time picker)
 */
import clsx from 'clsx';
import findKey from 'lodash/findKey';
import isEqual from 'lodash/isEqual';
import React, {useState, useRef, useMemo, useEffect} from 'react';

import {emitTrackEvent, rafRetry} from 'common/util/dom';
import {withStyles} from 'common/util/react';
import {ButtonSize, ButtonTheme, ButtonTheme as ButtonThemes} from 'common/components/button/constants';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {AUTOMATED_CUSTOM_PROPERTY_TRACKING} from 'common/constants';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {cohortsStore} from 'common/report/stores/cohorts';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {
  eventPropertyDefinitionsStore,
  groupPropertyDefinitionsStore,
  peoplePropertyDefinitionsStore,
} from 'common/report/stores/property-definitions';
import {getDateRangeTooltipRenderer} from 'common/widgets/date-range-preset-bar/date-range-tooltip';
import {DATE_RANGE_TYPES} from 'common/widgets/date-range-picker/models/date-range-factory';
import {DisplayContext as DateRangeDisplayContext, DisplayContext} from 'common/widgets/date-range-picker/models/types';
import {DisplayContext as FilterMenuDisplayContext} from 'common/widgets/property-filter-menu/models/types';
import {EntityTypeNameSingular} from 'common/report/util/saved-entities/types';
import {LabelTheme, SelectAllMechanism} from 'common/components/items-menu/types';
import {Directions} from 'common/components/drop-menu/types';
import {DisplaySize, Placement, PositioningStrategy} from 'common/components/tooltip/types';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {TooltipReact} from 'common/components/tooltip';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {StackableComponent, Visibility} from 'common/components/base/stackable';
import {ButtonReact} from 'common/components/button';
import {DropMenuReact} from 'common/components/drop-menu';
import {ItemsMenuReact} from 'common/components/items-menu';
import {SvgIconReact} from 'common/components/svg-icon';
import {ContextPanelReact, ContextPanelReactProps} from 'common/widgets/context-panel';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {useBehaviors} from 'common/contexts/react/behaviors-context';
import {useDomEvent} from 'common/hooks/useDomEvent';
import {useEventsController} from 'common/contexts/react/events-controller-context';
import {usePrevious} from 'common/hooks/usePrevious';
import {truncateMiddle} from 'common/util/string';

import {queryBuilderUtils} from '../query-builder/util';
import {MAX_LABEL_LENGTH, QueryBlockTheme, SelectorType, TRACKING_EVENTS} from './constants';
import {BreakdownMenuReact} from './breakdown-menu';
import {EventsMenu, EventsMenuReact} from './events-menu';
import {FilterMenuReact} from './filter-menu';
import {MultiEventsMenuReact} from './multi-events-menu';
import {TimeMenuReact} from './time-menu';
import {FormulasMenu, FormulasMenuReact} from './formulas-menu';
/*eslint-disable-next-line import/no-cycle*/
import {CountMenuReact} from './count-menu';
import css from './stylesheets/index.inline.styl';
import {updateEntityDescription} from './utils/util';
import {MenuType, QueryBlockParams} from './types';
import {IncludeCustomPropertyType, PropertyCombineMode} from './properties-menu/types';
import {BehaviorsMenu, BehaviorsMenuReact} from './behaviors-menu';
import {ChildMenuInterface, ContextPanelRenderer} from './child-menu';
import {PropertiesMenuReact} from './properties-menu';

// Static definition of all registered child menus
const MENU_TYPE_MAP: Partial<Record<MenuType, any>> = {
  [MenuType.Behaviors]: BehaviorsMenu,
  [MenuType.Events]: EventsMenu,
  [MenuType.Formulas]: FormulasMenu,
};

function QueryBlock(props: QueryBlockParams) {
  const rootEl = useRef<HTMLDivElement>(null);
  const contextPanelVisibilityTimerId = useRef<NodeJS.Timeout>(null);
  const menuClosedTimestamp = useRef<number>();

  const currentProject = useCurrentProject();
  const metrics = useMetrics();
  const behaviors = useBehaviors();
  const eventsController = useEventsController();

  const [childMenu, setChildMenu] = useState<ChildMenuInterface>(null);
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(props.open);
  const [openedMenuForModal, setOpenedMenuForModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tooltipData, setTooltipData] = useState(null);
  const [keydownProxyEl, setKeydownProxyEl] = useState(null);
  const [isTimePickerEnabled, setTimePickerEnabled] = useState(false);

  const previousOpenProp = usePrevious(props.open);

  const onChildMenuUpdate = useChildMenuSync(childMenu);

  const clearContextPanelVisibilityTimer = () => {
    if (contextPanelVisibilityTimerId.current) {
      clearTimeout(contextPanelVisibilityTimerId.current);
    }
    contextPanelVisibilityTimerId.current = null;
  };

  useEffect(() => {
    if (typeof eventsController.loading === `undefined`) {
      eventsController.fetchData();
    }
  }, [eventsController]);

  useEffect(() => {
    return () => {
      clearContextPanelVisibilityTimer();
    };
  }, []);

  useDomEvent(rootEl.current, `openContextMenu`, props.onOpenContextMenu);
  useDomEvent(rootEl.current, `closeContextMenu`, props.onCloseContextMenu);
  useDomEvent(rootEl.current, `track`, props.onTrack);
  useDomEvent(rootEl.current, `savedCustomEvent`, props.onSavedCustomEvent);

  const projectId = Number(props.projectId || getProjectId());
  const workspaceId = Number(props.workspaceId || getWorkspaceId());

  let displayContext = props.displayContext || DateRangeDisplayContext.Default;
  // TODO(go/j/AE-315): Cleanup once minute time picker launches.
  // This is to enable hiding hour/minute selection behind a feature-flag
  if (isTimePickerEnabled && displayContext === DisplayContext.FullLength) {
    displayContext = DisplayContext.FullLengthHourMinute;
  } else if (isTimePickerEnabled && displayContext === DisplayContext.PropertyFilter) {
    displayContext = DisplayContext.PropertyFilterHourMinute;
  }
  const dateRangeClass = DATE_RANGE_TYPES[displayContext];

  const selected = useMemo(() => {
    const selected = props.selected;

    switch (props.type) {
      case MenuType.Events:
        return props.multiple ? new Set(selected || []) : selected;

      case MenuType.Filter:
        return (selected && new PropertyFilter(selected)) || selected;

      case MenuType.Time:
        return selected ? new DATE_RANGE_TYPES[displayContext || DateRangeDisplayContext.FullLength](selected) : null;

      default:
        return selected;
    }
  }, [props.selected, props.type, props.multiple, props.displayContext]);

  const isTimeSelector = props.type === MenuType.Time;

  // update internal open state if the external one changes
  useEffect(() => {
    if (typeof props.open !== `undefined` && props.open !== previousOpenProp) {
      setIsMenuOpen(props.open);
    }
  }, [props.open, previousOpenProp]);

  const openMenu = () => {
    if (!isMenuOpen) {
      setIsMenuOpen(true);
      setTooltipData(null);
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setOpenedMenuForModal(false);
    setTooltipData(null);
    menuClosedTimestamp.current = Date.now();
    childMenu?.handleCloseMenu?.();
    props.onClose?.();
  };

  const dispatchSelect = (detail) => {
    props.onSelect?.(detail);

    if (detail.commit ?? childMenu?.shouldCloseOnSelect?.()) {
      closeMenu();
    }
  };

  const track = (event, props, cancelable = true) => {
    if (!props.trackName) {
      return;
    }

    emitTrackEvent(rootEl.current, {
      eventName: event,
      props: {
        ...props,
        project_id: projectId,
        tab: props.trackName,
      },
      cancelable,
    });
  };

  const delayShowContextPanel = () => {
    clearContextPanelVisibilityTimer();
    contextPanelVisibilityTimerId.current = setTimeout(() => {
      if (isMenuOpen) {
        return;
      }

      const tooltipData = childMenu?.getSelectedHoverTooltipData?.();
      props.onOpenContextMenu?.();
      rootEl.current.dispatchEvent(new CustomEvent(`openContextMenu`, {detail: {isOpen: true}, bubbles: true}));
      setTooltipData(tooltipData);

      if (tooltipData) {
        // Setting cancelable to false is needed to prevent mp-query-multi-block from swallowing the event
        const cancelable = false;
        track(
          TRACKING_EVENTS.HOVER_CONTEXT_PANEL,
          {
            'Entity name': tooltipData.header,
            'Query block type': props.type,
            'Has description': !!tooltipData.description,
            ...(childMenu?.getSelectedTrackingProps?.() || {}),
          },
          cancelable,
        );
      }
    }, 375);
  };

  const updateCohortDescription = (detail) => {
    const cohortStoreKey = detail.item.id;
    const descriptionBeforeUpdate = (cohortsStore.value[cohortStoreKey] as any).description;
    const cohortPropertyUpdate = {description: detail.description};

    const updateRequestObj = {
      projectId,
      workspaceId,
      cohortID: detail.item.id,
      cohort: cohortPropertyUpdate,
    };
    const undoRequestObj = {...updateRequestObj, cohort: {description: descriptionBeforeUpdate}};
    updateEntityDescription({
      entityStore: cohortsStore,
      newDescription: detail.description,
      storeKey: cohortStoreKey,
      updateRequestObj,
      undoRequestObj,
      entityType: `cohort property`,
      addToast,
      track,
    });
  };

  const updatePropertyDescription = (detail) => {
    const propertyStoreKey = detail.item.name;
    let propertyStore;

    if (detail.item.dataGroupId) {
      propertyStore = groupPropertyDefinitionsStore;
    } else {
      propertyStore =
        detail.item.type === EntityTypeNameSingular.EventProperty
          ? eventPropertyDefinitionsStore
          : peoplePropertyDefinitionsStore;
    }

    const descriptionBeforeUpdate = propertyStore.value[propertyStoreKey].description;

    let propertyDefinition;

    if (detail.item.id) {
      propertyDefinition = {id: detail.item.id, description: detail.description, dataGroupId: detail.item.dataGroupId};
    } else {
      propertyDefinition = {
        name: detail.item.name,
        resourceType: detail.item.type === EntityTypeNameSingular.EventProperty ? `Event` : `User`,
        description: detail.description,
        dataGroupId: detail.item.dataGroupId,
      };
    }

    const updateRequestObj = {
      projectId,
      workspaceId,
      propertyDefinition,
    };

    const undoRequestObj = {
      ...updateRequestObj,
      propertyDefinition: {...updateRequestObj.propertyDefinition, description: descriptionBeforeUpdate},
    };

    updateEntityDescription({
      entityStore: propertyStore,
      newDescription: detail.description,
      storeKey: propertyStoreKey,
      updateRequestObj,
      undoRequestObj,
      entityType: detail.item.type === EntityTypeNameSingular.EventProperty ? `event property` : `user property`,
      addToast,
      track,
    });
  };

  const updateCustomPropertyDescription = async (detail) => {
    const customPropertyId = detail.item.id;

    const customProperty = Object.assign(
      {},
      {...customPropertiesStore.value[customPropertyId], description: detail.description},
    );
    let extraProps = {};
    if (customProperty.pre_populated_entity_id) {
      extraProps = {
        ...extraProps,
        [AUTOMATED_CUSTOM_PROPERTY_TRACKING]: customProperty.pre_populated_entity_id,
      };
    }
    const oldDescription = customPropertiesStore.value[customPropertyId].description;
    const cancelable = false;
    track(
      TRACKING_EVENTS.EDIT_DESCRIPTION,
      {
        'New Description': detail.description,
        'old description': oldDescription,
        'access point': `context panel`,
        'Entity Type': `custom property`,
        ...extraProps,
      },
      cancelable,
    );
    try {
      await customPropertiesStore.put(customPropertyId, {
        projectId,
        workspaceId,
        customPropertyId,
        customProperty,
      });
      const undoCallback = async () => {
        await customPropertiesStore.put(customPropertyId, {
          projectId,
          workspaceId,
          customPropertyId,
          customProperty: {...customProperty, description: oldDescription},
        });
      };
      addToast({
        message: `'${detail.item.name}' has been updated`,
        type: ToastType.SUCCESS,
        undoable: true,
        undoCallback,
      });
    } catch (err) {
      addToast({message: `Failed to update the description`, type: ToastType.ERROR});
    }
  };

  // Delay closing the context panel so that when moving the mouse from the
  // query block to the context panel, the context doesn't get closed.
  const delayHideContextPanel = () => {
    clearContextPanelVisibilityTimer();
    contextPanelVisibilityTimerId.current = setTimeout(() => {
      if (isMenuOpen) {
        return;
      }
      setTooltipData(null);
      props.onCloseContextMenu?.();
      rootEl.current.dispatchEvent(new CustomEvent(`closeContextMenu`, {detail: {isOpen: false}, bubbles: true}));
    }, 100);
  };

  const hasNewUI = queryBuilderUtils.isCoreReport();

  const showInput = !props.inputOnly;
  const readonly = props.readonly || childMenu?.canOpenMenu?.() === false;

  // note: only relevant for the time menu at the moment
  const menuHasCustomWidth = props.type === MenuType.Time;

  const selectorTheme = useMemo(() => {
    let theme = props.theme;
    const isDefaultTheme = props.theme === ButtonTheme.Primary;
    const selectorType = props.selectorType;

    const buttonTheme = findKey(ButtonTheme, (value) => value === theme);

    // validate button theme and use QueryBlockTheme.Secondary as a default
    if (selectorType === SelectorType.Button) {
      if (!buttonTheme) {
        throw Error(`Unknown button theme`);
      }

      theme = isDefaultTheme ? QueryBlockTheme.Secondary : theme;
    }

    if (props.readonly && theme !== QueryBlockTheme.QueryBuilder) {
      theme = QueryBlockTheme.Tertiary;
    }

    return theme;
  }, [props.theme, props.selectorType, props.readonly]);
  const selectorType = props.selectorType;
  const prefixLabel = props.prefixLabel ?? childMenu?.getPrefixLabel?.();
  const selectedLabel = truncateMiddle(childMenu?.selectedLabel?.(), MAX_LABEL_LENGTH) || selected?.label;
  const headerSegments = props.headerText
    ? [
        {
          type: `text`,
          value: props.headerText,
        },
      ]
    : props.headerSegments || [];
  const tooltipProps = props.tooltip;

  const handleCancel = () => closeMenu();

  const handleChildMenuOpenedModal = () => {
    setIsModalOpen(true);
  };

  const handleChildMenuClosedModal = () => {
    setIsModalOpen(false);
    if (openedMenuForModal) {
      closeMenu();
    }
  };

  // Handle actions from the context tooltip attached to the query block. First, give the
  // child menu a chance to handle the action. If it's not handled by child menu, then
  // handle the action directly here.
  const handleTooltipContextAction = (ev) => {
    const {handled, openedModal} = childMenu?.handleTooltipContextAction?.(ev) || {
      handled: false,
      openedModal: false,
    };
    if (!handled) {
      throw Error(`unhandled context panel action`);
    }
    setTooltipData(null);

    // If a modal that lives in the child menu is open, then the child menu has to be open.
    // This can happen if you click the edit action on the context panel attached to the query block.
    // This will open the child menu's modal. However, we also have to force open the child menu,
    // otherwise the modal would be hidden.
    if (openedModal) {
      setIsMenuOpen(true);
      setOpenedMenuForModal(true);
      setIsModalOpen(true);
    }
  };

  const handleTooltipContextEditDescription = (ev) => {
    const detail = ev.detail;
    switch (detail.item.type) {
      case EntityTypeNameSingular.Cohort:
        updateCohortDescription(detail);
        break;
      case EntityTypeNameSingular.EventProperty:
        updatePropertyDescription(detail);
        break;
      case EntityTypeNameSingular.UserProperty:
        updatePropertyDescription(detail);
        break;
      case EntityTypeNameSingular.CustomProperty:
        updateCustomPropertyDescription(detail);
        break;
      default:
        break;
    }
  };

  const handleSelect = (ev) => dispatchSelect(ev.detail);

  const handleLabelClick = () => {
    if (!props.disabled && !props.readonly && childMenu.canOpenMenu()) {
      openMenu();
    }
  };

  const handleLabelMouseEnter = () => {
    // This is a a heuristic to prevent the following behavior: The user click the mp-query-block button and then
    // selects an item with just keyboard shortcuts. When the mp-items-menu is closed (because the mouse is still
    // on top of the mp-query-block button) this mouseenter event handler will retrigger causing
    // the context panel to be shown. I can't think of a better way to tell detect the scenario above, so using
    // the timestamp of closing menu as a heuristic to prevent reshowing the context panel within that timeframe.
    const menuWasRecentlyClosed =
      Number.isFinite(menuClosedTimestamp.current) && Date.now() - menuClosedTimestamp.current < 250;
    const anotherMenuIsOpen =
      StackableComponent.getStackingContextDepthForElement(rootEl.current) + 1 <=
      StackableComponent.getCurrentStackingContextDepth();
    if (!isMenuOpen && !menuWasRecentlyClosed && !anotherMenuIsOpen) {
      delayShowContextPanel();
    }
    childMenu?.handleLabelMouseEnter?.();
  };

  const handleLabelMouseLeave = () => {
    delayHideContextPanel();
    childMenu?.handleLabelMouseLeave?.();
  };

  const handleMenuChange = (ev) => {
    const {state} = ev.detail;
    if (state === Visibility.Closed) {
      closeMenu();
    } else if (keydownProxyEl && props.type !== MenuType.Time) {
      // This patch is equivalent fo throwing a sleep retry since
      // However, it is a goot fix while @pablo works on a real solution
      rafRetry(() => keydownProxyEl.focus());
    }

    if (state === Visibility.Open) {
      clearContextPanelVisibilityTimer();
      props.onOpen?.();
    }
  };

  const handleMouseDown = () => clearContextPanelVisibilityTimer();

  const handleDelete = (ev?: Event) => {
    ev?.stopPropagation();
    props.onRemove?.();
  };

  const handleContextPanelHoverIn = () => {
    rootEl.current.dispatchEvent(new CustomEvent(`mouseEnterContextMenu`, {bubbles: true}));
    clearContextPanelVisibilityTimer();
  };

  const handleContextPanelHoverOut = () => {
    rootEl.current.dispatchEvent(new CustomEvent(`mouseLeaveContextMenu`, {bubbles: true}));
    delayHideContextPanel();
  };

  /**
   * A given resource context panel (event, cohort or properties) needs to be defined
   * in the context of that menu type, to prevent having resource specific logic
   * in this main component
   */
  const renderContextPanelFromMenu = (panelProps: ContextPanelReactProps) => {
    const contextPanelRenderer: ContextPanelRenderer = MENU_TYPE_MAP[props.type]?.contextPanelRenderer;

    return (
      contextPanelRenderer &&
      contextPanelRenderer({
        availableContexts: {
          metrics,
          behaviors,
        },
        tooltipData,
        handleSelect: dispatchSelect,
        handleDelete,
        track,
        handleHoverIn: handleContextPanelHoverIn,
        handleHoverOut: handleContextPanelHoverOut,
        additionalArgs: {
          'all-data-groups': props.allDataGroups,
          'project-has-people': props.projectHasPeople,
          'track-name': props.trackName,
        },
        contextPanelProps: {
          ...panelProps,
          ...(queryBuilderUtils.isCoreReport() ? {positionParent: props.contextPanelParentElement} : {}),
        },
      })
    );
  };

  const contextPanelProps: Partial<ContextPanelReactProps> = {
    positionStrategy: PositioningStrategy.Fixed,
    placement: queryBuilderUtils.isCoreReport() ? Directions.Right : Directions.Down,
    offset: queryBuilderUtils.isCoreReport() ? 8 : 4,
    showArrow: true,
    elref: `contextPanel`,
  };

  return (
    <div
      ref={rootEl}
      className={clsx(
        `mp-query-block`,
        {
          'has-prefix-label': prefixLabel,
          'for-new-ui': hasNewUI,
          'has-header-segments': headerSegments.length,
          [`mp-query-theme-${selectorTheme}`]: true,
          [`type-${props.type}`]: true,
        },
        props.className,
        props.theme,
      )}
      elref={props.elref}
      tabIndex={props.tabIndex}
      onMouseDown={handleMouseDown}
      onClick={props.onClick}
    >
      <div className='mp-query-block-header-container'>
        {headerSegments.map((segment) =>
          segment.type === `text` ? (
            <div className='mp-query-block-header-segment-label'>{segment.value}</div>
          ) : segment.type === `select` ? (
            <>
              <div className='mp-query-block-header-drop-menu'>
                <DropMenuReact
                  open={isHeaderMenuOpen}
                  onChange={(ev) => {
                    if (ev.detail.state !== `open`) {
                      setIsHeaderMenuOpen(false);
                    }
                  }}
                >
                  <ItemsMenuReact
                    sections={[
                      {
                        // format for mp-items-menu
                        items: segment.items.map((item) => ({
                          ...item,
                          isSelected: item.value === segment.selectedItem.value,
                          labelTheme: LabelTheme.Strong,
                        })),
                      },
                    ]}
                    onClickItem={(ev) => {
                      setIsHeaderMenuOpen(false);
                      props.onSelectHeaderItem?.(ev.detail.item);
                    }}
                  />
                </DropMenuReact>
              </div>
              <div
                className='mp-query-block-header-segment-selector-label'
                onClick={() => setIsHeaderMenuOpen((open) => !open)}
              >
                {segment.selectedItem.label}
                <SvgIconReact icon='caret-down' />
              </div>
            </>
          ) : null,
        )}
        {/*information label*/}
        {props.helpText ? (
          <div className='header-info'>
            <SvgIconReact icon='info' />
            <TooltipReact className='help-text'>{props.helpText}</TooltipReact>
          </div>
        ) : null}
      </div>
      {/*.mp-query-block-unit*/}
      {selectorType !== `button` && prefixLabel ? (
        <div className='mp-query-block-unit' elref='blockUnitLabel'>
          {prefixLabel}
        </div>
      ) : null}
      <div
        className={clsx(`mp-query-block-selector`, {
          'mp-query-editing': showInput && isMenuOpen,
        })}
        onClick={handleLabelClick}
        elref='selectorLabel'
      >
        {tooltipData && !isMenuOpen && !openedMenuForModal ? (
          renderContextPanelFromMenu(contextPanelProps) ?? (
            <ContextPanelReact
              {...contextPanelProps}
              data={tooltipData}
              positionParent={props.contextPanelParentElement}
              onHoverIn={handleContextPanelHoverIn}
              onHoverOut={handleContextPanelHoverOut}
              onTooltipContextAction={handleTooltipContextAction}
              onTooltipContextEditDescription={handleTooltipContextEditDescription}
            />
          )
        ) : !isMenuOpen && tooltipProps ? (
          <TooltipReact size={DisplaySize.Small} placement={Placement.Right} {...tooltipProps} />
        ) : null}
        {/*The mouseenter and mouseleave events must be on this element rather than the parent*/}
        {/*element, since the parent element includes the child menu. Placing these events on the*/}
        {/*parent element causes problems when hovering over the context panel from the child*/}
        {/*menu in some cases (e.g. for mp-events-menu)*/}
        <div
          className='mp-query-block-selector-inner'
          elref='selectorLabelInner'
          onMouseEnter={handleLabelMouseEnter}
          onMouseLeave={handleLabelMouseLeave}
        >
          <BlockSelector
            autofocus={props.autofocus && !isMenuOpen}
            isMenuOpen={isMenuOpen}
            showInput={showInput}
            selectorTheme={selectorTheme}
            prefixLabel={prefixLabel}
            removable={props.removable}
            browserTooltipLabel={childMenu?.selectedBrowserTooltipLabel?.()}
            label={showInput ? selectedLabel || props.label : props.label}
            leftIcon={props.selectorIcon ?? childMenu?.selectedLeftIcon?.()}
            rightIcon={props.selectorRightIcon ?? childMenu?.selectedRightIcon?.()}
            onClickRemove={handleDelete}
            selectorType={props.selectorType}
            theme={props.theme as ButtonTheme}
            readonly={readonly}
            buttonSize={props.buttonSize}
            disabled={props.disabled}
            labelRenderer={props.labelRenderer}
            isUnsavedCustomEntity={childMenu?.isUnsavedCustomEntity?.()}
            highlightSelections={props.highlightSelections && childMenu?.getSelections?.()}
          />
          {isTimeSelector && selected && (
            <TooltipReact
              elref='dateRangeTooltip'
              placement={queryBuilderUtils.isCoreReport() ? Placement.Bottom : Placement.Top}
              lazyContentRenderer={getDateRangeTooltipRenderer(
                new dateRangeClass(selected),
                currentProject.get().value,
              )}
            />
          )}
        </div>
        <div
          className={clsx(`mp-query-drop-menu-container`, {
            'for-new-ui': hasNewUI,
            [`for-${props.type}-menu`]: true,
          })}
        >
          <DropMenuReact
            className={clsx({
              'custom-width': menuHasCustomWidth,
            })}
            open={isMenuOpen}
            direction={props.menuAlignment}
            preventClose={props.preventClose || isModalOpen}
            preventCloseSelectors={[
              `#chmln`,
              ...(props.type === MenuType.Properties ? [`.mp-query-block-selector`] : []),
              `mp-custom-property-modal`,
              `mp-tooltip`, // prevent clicks on tooltips from closing the menu, allows users to click links within tooltips
            ]}
            // TODO investigate further why 2+ immediate clicks locks out only properties menu,
            // for now don't close the opened properties menu on subsequent selector clicks
            {...(hasNewUI ? {offset: props.menuOffset} : {})}
            onChange={handleMenuChange}
          >
            {(() => {
              const open = isMenuOpen && !openedMenuForModal;
              const key = props.type;
              let otherOptions;

              if (props.otherGroupByOptions?.length) {
                otherOptions = props.otherGroupByOptions;
              } else if (props.otherFilterOptions?.length) {
                otherOptions = props.otherFilterOptions;
              } else if (props.otherAggregationOptions?.length) {
                otherOptions = props.otherAggregationOptions;
              }

              const setRef = (ref) => {
                if (ref && ref !== childMenu) {
                  setChildMenu(ref);
                }
              };

              switch (props.type) {
                case MenuType.Time:
                  return (
                    <TimeMenuReact
                      key={key}
                      ref={setRef}
                      selected={selected}
                      displayContext={DateRangeDisplayContext[displayContext]}
                      selectorType={props.selectorType}
                      projectAsOfDate={props.projectAsOfDate}
                      maxDataHistoryDays={props.maxDataHistoryDays}
                      isTimePickerEnabled={isTimePickerEnabled}
                      open={open}
                      eventNames={props.eventNames}
                      readonly={props.readonly}
                      trackName={props.trackName}
                      setTimePickerEnabled={setTimePickerEnabled}
                      onSelect={handleSelect}
                      onCancel={handleCancel}
                      onRender={onChildMenuUpdate}
                    />
                  );
                case MenuType.Behaviors:
                  return (
                    <BehaviorsMenuReact
                      key={key}
                      ref={setRef}
                      keydownProxyEl={keydownProxyEl}
                      onSetKeydownProxyEl={setKeydownProxyEl}
                      selected={selected}
                      metricType={props.metricType}
                      open={open}
                      eventNames={props.eventNames}
                      projectId={props.projectId}
                      readonly={props.readonly}
                      trackName={props.trackName}
                      userId={props.userId}
                      workspaceId={props.workspaceId}
                      onSelect={handleSelect}
                      onCreate={props.onCreate}
                      onRender={onChildMenuUpdate}
                    />
                  );
                case MenuType.Events:
                case MenuType.MultiEvents: {
                  const EventsMenu =
                    props.type === MenuType.MultiEvents || props.multiple ? MultiEventsMenuReact : EventsMenuReact;
                  return (
                    <EventsMenu
                      key={key}
                      ref={setRef}
                      isModalOpen={isModalOpen}
                      onModalOpen={handleChildMenuOpenedModal}
                      onModalClose={handleChildMenuClosedModal}
                      keydownProxyEl={keydownProxyEl}
                      onSetKeydownProxyEl={setKeydownProxyEl}
                      selected={selected}
                      tooltipData={tooltipData}
                      onTooltipChange={setTooltipData}
                      otherOptions={otherOptions}
                      inverted={props.inverted}
                      includeHidden={props.includeHidden}
                      allDataGroups={props.allDataGroups}
                      dataGroupId={props.dataGroupId}
                      excludeAutotrackEvents={props.excludeAutotrackEvents}
                      excludeCustomEvents={props.excludeCustomEvents}
                      hideEditControls={props.hideEditControls}
                      multiple={props.multiple}
                      open={open}
                      projectHasPeople={props.projectHasPeople}
                      readonly={props.readonly}
                      showAllEvents={props.showAllEvents}
                      showAnyEvent={props.showAnyEvent}
                      showSessionEvent={props.showSessionEvent}
                      showSpecialDatasetProperty={props.showSpecialDatasetProperty}
                      showTopEvents={props.showTopEvents}
                      trackName={props.trackName}
                      eventNames={props.eventNames}
                      projectId={props.projectId}
                      userId={props.userId}
                      workspaceId={props.workspaceId}
                      onSelect={handleSelect}
                      onDelete={handleDelete}
                      onRender={onChildMenuUpdate}
                    />
                  );
                }
                case MenuType.Properties:
                  return (
                    <PropertiesMenuReact
                      key={key}
                      ref={setRef}
                      isModalOpen={isModalOpen}
                      onModalOpen={handleChildMenuOpenedModal}
                      onModalClose={handleChildMenuClosedModal}
                      listProperty={props.listProperty}
                      getSortedMenuSections={props.getSortedMenuSections}
                      selected={selected}
                      cohortMultiselect={props.cohortMultiselect}
                      applyButtonText={props.applyButtonText}
                      minSelection={props.minSelection}
                      allowBehavioralPropertyCreation={props.allowBehavioralPropertyCreation}
                      allowTypecastPillAction={props.allowTypecastPillAction}
                      enableWorkspaceFilterRestrictions={props.enableWorkspaceFilterRestrictions}
                      disabledItems={props.disabledItems}
                      includeCustomProperties={props.includeCustomProperties as any}
                      showBorrowedProperties={props.showBorrowedProperties}
                      excludeDimensionProperties={props.excludeDimensionProperties}
                      excludeLookupTableProperties={props.excludeLookupTableProperties}
                      propertyCombineMode={props.propertyCombineMode as PropertyCombineMode}
                      specialCohorts={props.specialCohorts}
                      otherOptions={otherOptions}
                      selectAllMechanism={props.selectAllMechanism}
                      showSelectAll={props.showSelectAll}
                      selectAllDisabled={props.selectAllDisabled}
                      hideRecentlyViewed={props.hideRecentlyViewed}
                      projectIsFreePlan={props.projectIsFreePlan}
                      selectableSensitiveProperties={props.selectableSensitiveProperties}
                      menuItemTypes={props.menuItemTypes}
                      contextPanelAlignment={props.contextPanelAlignment}
                      typeFilter={props.typeFilter}
                      allDataGroups={props.allDataGroups}
                      showAllDataGroups={props.showAllDataGroups}
                      dataGroupId={props.dataGroupId}
                      eventNames={props.eventNames}
                      hideAllPropertiesSelect={props.hideAllPropertiesSelect}
                      hideEditControls={props.hideEditControls}
                      hideEvents={props.hideEvents}
                      inlineCohortsOverride={props.inlineCohortsOverride}
                      multiple={props.multiple}
                      open={open}
                      projectHasCohorts={props.projectHasCohorts}
                      projectHasPeople={props.projectHasPeople}
                      projectId={props.projectId}
                      readonly={props.readonly}
                      showAllEvents={props.showAllEvents}
                      showAnyEvent={props.showAnyEvent}
                      showSessionEvent={props.showSessionEvent}
                      showSpecialDatasetProperty={props.showSpecialDatasetProperty}
                      showSpecialDatePropertyUnit={props.showSpecialDatePropertyUnit}
                      showTopEvents={props.showTopEvents}
                      supportB2bCohorts={props.supportB2bCohorts}
                      trackName={props.trackName}
                      userId={props.userId}
                      workspaceId={props.workspaceId}
                      onSelect={(item) => handleSelect({detail: item})}
                      onDelete={handleDelete}
                      onTooltipContextEditDescription={handleTooltipContextEditDescription}
                      onRender={onChildMenuUpdate}
                    />
                  );
                case MenuType.Count:
                  return (
                    <CountMenuReact
                      key={key}
                      ref={setRef}
                      listProperty={props.listProperty}
                      chartType={props.chartType}
                      dataGroups={props.dataGroups}
                      dateRange={props.dateRange}
                      dateUnit={props.dateUnit}
                      advancedMenuSections={props.advancedMenuSections}
                      advancedMenuLabel={props.advancedMenuLabel}
                      countUsersOnceState={props.countUsersOnceState}
                      otherAggregationOptions={props.otherAggregationOptions}
                      dataGroupId={props.dataGroupId}
                      metricType={props.metricType}
                      open={open}
                      selected={props.selected}
                      trackName={props.trackName}
                      enableWorkspaceFilterRestrictions={props.enableWorkspaceFilterRestrictions}
                      includeCustomProperties={props.includeCustomProperties as IncludeCustomPropertyType}
                      showBorrowedProperties={props.showBorrowedProperties}
                      allDataGroups={props.allDataGroups}
                      excludeDimensionProperties={props.excludeDimensionProperties}
                      excludeLookupTableProperties={props.excludeLookupTableProperties}
                      hideAllPropertiesSelect={props.hideAllPropertiesSelect}
                      hideEditControls={props.hideEditControls}
                      hideEvents={props.hideEvents}
                      inlineCohortsOverride={props.inlineCohortsOverride}
                      multiple={props.multiple}
                      projectHasCohorts={props.projectHasCohorts}
                      projectHasPeople={props.projectHasPeople}
                      showAllEvents={props.showAllEvents}
                      showAnyEvent={props.showAnyEvent}
                      showSessionEvent={props.showSessionEvent}
                      showSpecialDatasetProperty={props.showSpecialDatasetProperty}
                      showSpecialDatePropertyUnit={props.showSpecialDatePropertyUnit}
                      showTopEvents={props.showTopEvents}
                      supportB2bCohorts={props.supportB2bCohorts}
                      eventNames={props.eventNames}
                      projectId={props.projectId}
                      readonly={props.readonly}
                      userId={props.userId}
                      workspaceId={props.workspaceId}
                      onSelect={(item) => handleSelect({detail: item})}
                      onRender={onChildMenuUpdate}
                    />
                  );
                case MenuType.Filter:
                  return (
                    <FilterMenuReact
                      key={key}
                      ref={setRef}
                      keydownProxyEl={keydownProxyEl}
                      listProperty={props.listProperty}
                      selected={selected}
                      enableEnhancedListFilters={props.enableEnhancedListFilters}
                      enableWorkspaceFilterRestrictions={props.enableWorkspaceFilterRestrictions}
                      disableValueSelection={props.disableValueSelection}
                      includeCustomProperties={props.includeCustomProperties}
                      showBorrowedProperties={props.showBorrowedProperties}
                      displayContext={props.displayContext as FilterMenuDisplayContext}
                      disabledItems={props.disabledItems}
                      otherFilterOptions={props.otherFilterOptions}
                      allDataGroups={props.allDataGroups}
                      dataGroupId={props.dataGroupId}
                      eventNames={props.eventNames}
                      excludeDimensionProperties={props.excludeDimensionProperties}
                      excludeLookupTableProperties={props.excludeLookupTableProperties}
                      hideEditControls={props.hideEditControls}
                      hideEvents={props.hideEvents}
                      inlineCohortsOverride={props.inlineCohortsOverride}
                      open={open}
                      projectHasCohorts={props.projectHasCohorts}
                      projectHasPeople={props.projectHasPeople}
                      projectId={props.projectId}
                      readonly={props.readonly}
                      showSpecialDatasetProperty={props.showSpecialDatasetProperty}
                      supportB2bCohorts={props.supportB2bCohorts}
                      trackName={props.trackName}
                      userId={props.userId}
                      workspaceId={props.workspaceId}
                      onSelect={handleSelect}
                      onRender={onChildMenuUpdate}
                    />
                  );
                case MenuType.Breakdown:
                  return (
                    <BreakdownMenuReact
                      key={key}
                      ref={setRef}
                      listProperty={props.listProperty}
                      dataGroupId={props.dataGroupId}
                      open={open}
                      projectId={props.projectId}
                      selected={props.selected}
                      showSpecialDatePropertyUnit={props.showSpecialDatePropertyUnit}
                      workspaceId={props.workspaceId}
                      includeCustomProperties={props.includeCustomProperties as IncludeCustomPropertyType}
                      showBorrowedProperties={props.showBorrowedProperties}
                      allDataGroups={props.allDataGroups}
                      excludeDimensionProperties={props.excludeDimensionProperties}
                      excludeLookupTableProperties={props.excludeLookupTableProperties}
                      hideAllPropertiesSelect={props.hideAllPropertiesSelect}
                      hideEditControls={props.hideEditControls}
                      hideEvents={props.hideEvents}
                      inlineCohortsOverride={props.inlineCohortsOverride}
                      multiple={props.multiple}
                      projectHasCohorts={props.projectHasCohorts}
                      projectHasPeople={props.projectHasPeople}
                      showAllEvents={props.showAllEvents}
                      showAnyEvent={props.showAnyEvent}
                      showSessionEvent={props.showSessionEvent}
                      showSpecialDatasetProperty={props.showSpecialDatasetProperty}
                      showTopEvents={props.showTopEvents}
                      supportB2bCohorts={props.supportB2bCohorts}
                      eventNames={props.eventNames}
                      readonly={props.readonly}
                      trackName={props.trackName}
                      onSelect={handleSelect}
                      onRender={onChildMenuUpdate}
                    />
                  );
                case MenuType.Formulas:
                  return (
                    <FormulasMenuReact
                      key={key}
                      ref={setRef}
                      selected={selected}
                      open={open}
                      eventNames={props.eventNames}
                      projectId={props.projectId}
                      readonly={props.readonly}
                      trackName={props.trackName}
                      userId={props.userId}
                      workspaceId={props.workspaceId}
                      keydownProxyEl={keydownProxyEl}
                      onSetKeydownProxyEl={setKeydownProxyEl}
                      onSelect={handleSelect}
                      onCreate={props.onCreate}
                      onRender={onChildMenuUpdate}
                    />
                  );
              }

              return null;
            })()}
          </DropMenuReact>
        </div>
      </div>
    </div>
  );
}

const defaultProps: QueryBlockParams = {
  advancedMenuSections: [],
  buttonSize: ButtonSize.Medium,
  dataGroups: [],
  disabledItems: {},
  excludedElements: [],
  includeCustomProperties: IncludeCustomPropertyType.None,
  menuAlignment: Directions.Down,
  menuOffset: 0,
  otherAggregationOptions: [],
  otherFilterOptions: [],
  otherGroupByOptions: [],
  selectAllMechanism: SelectAllMechanism.Full,
  selectorType: SelectorType.Text,
  theme: ButtonThemes.Primary,
  typeFilter: [],
};

QueryBlock.defaultProps = defaultProps;

function useChildMenuSync(childMenu: ChildMenuInterface) {
  const [, forceRender] = useState({});
  const waitingForRender = useRef(false);

  useEffect(() => {
    waitingForRender.current = false;
  });

  const getChildMenuState = () => ({
    selectedLabel: childMenu?.selectedLabel?.(),
    getPrefixLabel: childMenu?.getPrefixLabel?.(),
    selectedBrowserTooltipLabel: childMenu?.selectedBrowserTooltipLabel?.(),
    selectedLeftIcon: childMenu?.selectedLeftIcon?.(),
    selectedRightIcon: childMenu?.selectedRightIcon?.(),
  });

  const lastRenderState = getChildMenuState();

  return () => {
    // since we're depending on child menu state, we need to force a re-render here whenever certain
    // state updates there. this is a hack to help with ui weirdness but the whole relationship needs
    // to be seriously refactored.
    const latestChildMenuState = getChildMenuState();

    if (!isEqual(lastRenderState, latestChildMenuState) && !waitingForRender.current) {
      forceRender({});
      waitingForRender.current = true;
    }
  };
}

function SelectorLabel(props: {label: string; highlightSelections?: string[]; isUnsavedCustomEntity: boolean}) {
  // if selections are given, highlight them
  if (props.highlightSelections?.length) {
    const indices = new Set();
    const words = props.label.split(` `);
    for (const selection of props.highlightSelections) {
      const targetWords = selection.split(` `);
      for (let i = 0; i < words.length; i++) {
        if (words.slice(i, i + targetWords.length).join(` `) === targetWords.join(` `)) {
          for (let j = i; j < i + targetWords.length; j++) {
            indices.add(j);
          }
          break;
        }
      }
    }

    return (
      <span className={clsx({'is-unsaved': props.isUnsavedCustomEntity})}>
        {words.map((word, idx) => {
          if (indices.has(idx)) {
            return <b>{word} </b>;
          }
          return `${word} `;
        })}
      </span>
    );
  }

  return (
    <span id='blockSelector' className={clsx({'is-unsaved': props.isUnsavedCustomEntity})}>
      {props.label + (props.isUnsavedCustomEntity ? `*` : ``)}
    </span>
  );
}

function BlockSelector(props: {
  autofocus: boolean;
  isMenuOpen: boolean;
  selectorTheme: QueryBlockTheme;
  prefixLabel: string;
  removable: boolean;
  showInput: boolean;
  readonly: boolean;
  label: string;
  browserTooltipLabel: string;
  leftIcon: string;
  disabled: boolean;
  rightIcon: string;
  selectorType: SelectorType;
  theme: ButtonTheme;
  buttonSize: ButtonSize;
  isUnsavedCustomEntity: boolean;
  highlightSelections?: string[];
  labelRenderer?: () => React.ReactNode;
  onClickRemove: () => void;
}) {
  const buttonRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (props.autofocus && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, []);

  const removeButtonEl = props.removable && (
    <div className='mp-query-block-remove-button' onClick={props.onClickRemove}>
      <SvgIconReact icon='circle-clear' />
    </div>
  );

  // TODO: the button theme type should be better separated from the query block theme type
  const allowedButtonThemes = [ButtonTheme.Select, ButtonTheme.QbSubitem];

  if (props.selectorType === `button` || allowedButtonThemes.includes(props.theme)) {
    return (
      <ButtonReact
        ref={buttonRef as any}
        className={clsx(`truncated`, {
          [`icon-${props.leftIcon}`]: !!props.leftIcon,
        })}
        disabled={props.disabled}
        icon={props.leftIcon || ``}
        theme={props.selectorTheme as ButtonTheme}
        size={props.buttonSize}
        notFocusable={props.readonly}
        pressed={props.isMenuOpen}
        readonly={props.readonly}
        elref='blockMenuLabelIcon'
        {...(props.browserTooltipLabel
          ? {
              title: props.browserTooltipLabel,
            }
          : {})}
      >
        <div
          className={clsx(`block-menu-label`, {
            'has-right-icon': !!props.rightIcon,
          })}
          elref='blockMenuLabel'
        >
          {props.labelRenderer ? (
            props.labelRenderer()
          ) : (
            <>
              {props.prefixLabel && <span className='label-prefix'>{props.prefixLabel}</span>}
              <SelectorLabel
                label={props.label || ``}
                highlightSelections={props.highlightSelections}
                isUnsavedCustomEntity={props.isUnsavedCustomEntity}
              />
            </>
          )}
        </div>
        {props.rightIcon && <SvgIconReact elref='rightIcon' icon={props.rightIcon as any} />}
        {removeButtonEl}
      </ButtonReact>
    );
  } else {
    // block label
    return (
      <div
        className={clsx(`mp-query-menu-label`, `block-menu-label`, {
          'is-disabled': props.disabled,
          'is-editing': props.showInput && props.isMenuOpen,
          [`is-theme-${props.selectorTheme}`]: true,
        })}
        elref='blockMenuLabel'
      >
        {props.leftIcon && <SvgIconReact icon={props.leftIcon as any} elref='blockMenuLabelIcon' />}
        {props.label}
        {props.rightIcon && <SvgIconReact icon={props.rightIcon as any} />}
        {removeButtonEl}
      </div>
    );
  }
}

export const QueryBlockReact = withStyles(QueryBlock, css);
